import { useEffect, useMemo, useState } from 'react';
import WebAppService from '../../../../services/WebAppService';
import IntakeService from '../../../../services/IntakeService';
import useAuthStore from '../../../../store/Auth';
import DebouncedSearchBar from '../../../common/DebouncedSearchBar/DebouncedSearchBar';
import Popover from '../../../ui/Popover/Popover';
import { EditIcon, PlusIcon, Profile } from '../../../../svgs/svgs';
import Spinner from '../../../ui/Loading/Spinner';
import PaginationArrows from '../../../ui/PaginationArrows/PaginationArrows';
import usePagination from '../../../../hooks/usePagination';
import type { Intake, Task } from '@/types';

interface AssignTaskProps {
  type?: 'task' | 'intake';
  selectedTask?: Task | null;
  selectedIntake?: Intake | null;
  onAssign?: () => void;
  /** When true, selecting a member does not call the API; the parent reads the selection via onMemberSelect. */
  deferAssignment?: boolean;
  /** Called with the selected member (or null for unassign) when deferAssignment is true. */
  onMemberSelect?: (member: { email: string; name: string } | null) => void;
}

export default function AssignTask({
  type = 'task',
  selectedTask,
  selectedIntake,
  onAssign = () => {},
  deferAssignment = false,
  onMemberSelect,
}: AssignTaskProps) {
  const { organization } = useAuthStore();
  const entityLabel = type === 'task' ? 'Task' : 'Intake';
  const isIntake = type === 'intake';
  const asString = (value: unknown): string =>
    typeof value === 'string' ? value : '';

  const getAssignedMember = (): { name: string; email: string } | null => {
    if (type === 'task') {
      const ownerName = asString(selectedTask?.task_owner_name);
      if (!ownerName) return null;

      return {
        name: ownerName,
        email: asString(selectedTask?.task_owner_email),
      };
    }

    const intake = selectedIntake;
    const intakeOwnerName =
      intake?.task_owner_name ||
      intake?.intake_owner_name ||
      intake?.assigned_to?.name;
    const intakeOwnerEmail =
      intake?.task_owner_email ||
      intake?.intake_owner_email ||
      intake?.assigned_to?.email;

    if (!intakeOwnerName) return null;

    return {
      name: asString(intakeOwnerName),
      email: asString(intakeOwnerEmail),
    };
  };

  const [assignedMember, setAssignedMember] = useState<{
    name: string;
    email: string;
  } | null>(getAssignedMember);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const limit = 5;

  const filteredMembers = useMemo(() => {
    const q = debouncedSearchTerm.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const name = (m.full_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`)
        .toLowerCase();
      const email = (m.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [members, debouncedSearchTerm]);

  const {
    paginatedItems,
    hasPrevious,
    hasNext,
    showingFrom,
    showingTo,
    totalItems,
    handleNextPage,
    handlePreviousPage,
    resetPage,
  } = usePagination<any>(filteredMembers, limit, 1);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const getOrgMembers = async () => {
    setLoading(true);
    try {
      let res;

      if (isIntake && selectedIntake) {
        // For intake, get team members only
        const teamId =
          typeof selectedIntake.team_id === 'string'
            ? selectedIntake.team_id
            : selectedIntake.team_id?._id;

        if (teamId) {
          res = await WebAppService.getMembersInATeam(teamId);
          const teamMembers = (res?.data || []).map((member: any) => ({
            ...member,
            full_name: `${member.first_name} ${member.last_name}`,
          }));
          setMembers(teamMembers);
        } else {
          setMembers([]);
        }
      } else {
        // For tasks, get all organization members
        res = await WebAppService.getMembersInOrg(organization?.id!, 1, 1000, '');
        const orgMembers = (Array.isArray(res?.data) ? res.data : res?.data?.data || []).map((member: any) => ({
          ...member,
          full_name:
            member.full_name ||
            `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim(),
        }));
        setMembers(orgMembers);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrgMembers();
  }, [isIntake, selectedIntake?._id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      resetPage();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setAssignedMember(getAssignedMember());
  }, [type, selectedTask, selectedIntake]);

  const handleAssign = async (member: any) => {
    setAssignedMember({
      name: member.full_name,
      email: member.email,
    });

    try {
      if (type === 'task') {
        await WebAppService.assignMemberToTask(selectedTask?._id!, {
          user_email: member.email,
        });
      } else {
        await IntakeService.assignIntake(selectedIntake?._id!, {
          user_email: member.email,
        });
      }

      onAssign?.();
    } catch (error) {
      console.error(error);
      setAssignedMember(getAssignedMember());
    }
  };

  const handleUnassign = async () => {
    const previousMember = assignedMember;
    setAssignedMember(null);

    try {
      if (type === 'task') {
        const res = await WebAppService.unassignMemberFromTask(
          selectedTask?._id!
        );
        console.log(res.data);
      } else {
        await IntakeService.unassignIntake(selectedIntake?._id!);
      }

      onAssign?.();
    } catch (error) {
      console.error(error);
      setAssignedMember(previousMember);
    }
  };

  const handleSelect = (member: any) => {
    if (deferAssignment) {
      if (member == null) {
        setAssignedMember(null);
        onMemberSelect?.(null);
      } else {
        setAssignedMember({ name: member.full_name, email: member.email });
        onMemberSelect?.({ name: member.full_name, email: member.email });
      }
      return;
    }

    if (member == null) {
      handleUnassign();
    } else {
      handleAssign(member);
    }
  };

  return (
    <Popover
      customContent
      customTrigger={
        <button className="w-full flex items-center gap-1 text-input-inactive-placeholder text-xs 2xl:text-sm p-1">
          <div className="w-full flex items-center justify-between gap-1">
            <div className="w-fit flex items-center gap-1">
              <div className="w-4">
                <Profile />
              </div>
              <p className="truncate line-clamp-1 max-w-[90px]">
                {assignedMember?.name || 'Unassigned'}
              </p>
            </div>
            <div className="w-4">
              <EditIcon />
            </div>
          </div>
        </button>
      }
      content={(close) => (
        <div className="w-full p-3">
          <div className="pb-2 border-b border-modal-stroke mb-4">
            <h3 className="text-text-primary-heading font-bold text-sm">
              {`Assign ${entityLabel}`}
            </h3>
            <p className="text-sm">
              {`Select a team member to own this ${entityLabel.toLowerCase()} and assign it to them`}
            </p>
          </div>

          <DebouncedSearchBar
            placeholder={`Search ${isIntake ? "team" :"organization"} members`}
            className="w-full max-w-[600px]"
            onSearch={handleSearchChange}
            value={searchTerm}
          />

          <div className="border border-modal-stroke mt-4 p-3 rounded-lg h-[300px]">
            <PaginationArrows
              className="mb-4"
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              showingFrom={showingFrom}
              showingTo={showingTo}
              totalItems={totalItems}
              itemLabel="members"
            />

            {loading ? (
              <div className="flex flex-col gap-2 w-full h-[80%] items-center justify-center">
                <div className="w-4">
                  <Spinner />
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {assignedMember && (
                  <button
                    className="w-full text-left px-2.5 py-2.5 rounded-md text-sm bg-[#121212] hover:bg-modal-input hover:pl-5 transition-all ease-in-out duration-300"
                    onClick={() => {
                      handleSelect(null);
                      close();
                    }}
                  >
                    Unassigned
                  </button>
                )}
                {paginatedItems.length === 0 ? (
                  <div className="text-center py-4 text-sm">
                    No members found.
                  </div>
                ) : (
                  paginatedItems.map((member, index) => (
                    <button
                      key={index}
                      className="w-full text-left px-2.5 py-2.5 rounded-md text-sm bg-[#121212] hover:bg-modal-input hover:pl-5 transition-all ease-in-out duration-300"
                      onClick={() => {
                        handleSelect(member);
                        close();
                      }}
                    >
                      {member.full_name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
}
